import { api } from "@/lib/api";

export interface HoroscopeSign {
  id: string;
  slug: string;
  name: string;
  symbol: string;
  element: "fire" | "earth" | "air" | "water";
  rulingPlanet: string;
  dateRangeLabel: string;
  colorHex: string | null;
  iconUrl: string | null;
  backgroundImageUrl: string | null;
  gradientTheme: string | null;
}

export interface UpdateHoroscopeSignData {
  backgroundImageUrl?: string | null;
}

export interface HoroscopeTranslationInput {
  languageCode: string;
  title?: string;
  readingText: string;
  thumbnailUrl?: string;
  isDefault?: boolean;
}

export interface HoroscopeEntry {
  id: string;
  zodiacSign: string;
  date: string;
  primaryLanguage: string;
  status:
    | "draft"
    | "submitted"
    | "changes_requested"
    | "published"
    | "archived"
    | "outdated";
  version: number;
  createdBy: string;
  createdByRole: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  archivedAt: string | null;
  internalNotes: string | null;
  reviewNote: string | null;
  tags: string[];
  translations: {
    id: string;
    languageCode: string;
    title: string | null;
    readingText: string;
    thumbnailUrl: string | null;
    isDefault: boolean;
  }[];
}

export interface CreateHoroscopeEntryData {
  zodiacSign: string;
  date: string;
  primaryLanguage: string;
  translations: HoroscopeTranslationInput[];
  internalNotes?: string;
  tags?: string[];
}

export interface UpdateHoroscopeEntryData {
  translations?: HoroscopeTranslationInput[];
  internalNotes?: string;
  reviewNote?: string;
  tags?: string[];
  primaryLanguage?: string;
}

export interface UpdateHoroscopeEntryStatusData {
  status: "submitted" | "draft" | "archived";
  reviewNote?: string;
}

export interface ReviewHoroscopeEntryData {
  action: "approve" | "changes_requested";
  note?: string;
}

export interface HoroscopeEntriesResponse {
  entries: HoroscopeEntry[];
  total: number;
}

export interface BulkCreateResponse {
  created: number;
  /** Existing draft entries that received new language translations. */
  updated?: number;
  skipped: number;
  errors: string[];
}

export const horoscopeService = {
  async getSigns(language: string = "en"): Promise<HoroscopeSign[]> {
    return api.get<HoroscopeSign[]>(
      `/horoscope/signs?language=${encodeURIComponent(language)}`,
    );
  },

  async updateSign(id: string, file: File): Promise<HoroscopeSign> {
    const formData = new FormData();
    formData.append("backgroundImage", file);
    return api.patch<HoroscopeSign>(`/horoscope/signs/${id}`, formData);
  },

  async getEntries(filters?: {
    zodiacSign?: string;
    date?: string;
    status?: string;
    language?: string;
    limit?: number;
    offset?: number;
  }): Promise<HoroscopeEntriesResponse> {
    const params = new URLSearchParams();
    if (filters?.zodiacSign) params.set("zodiacSign", filters.zodiacSign);
    if (filters?.date) params.set("date", filters.date);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.language) params.set("language", filters.language);
    if (filters?.limit) params.set("limit", filters.limit.toString());
    if (filters?.offset) params.set("offset", filters.offset.toString());

    return api.get<HoroscopeEntriesResponse>(
      `/horoscope/entries?${params.toString()}`,
    );
  },

  async getEntryById(id: string): Promise<HoroscopeEntry> {
    return api.get<HoroscopeEntry>(`/horoscope/entries/${id}`);
  },

  async createEntry(data: CreateHoroscopeEntryData): Promise<HoroscopeEntry> {
    return api.post<HoroscopeEntry>("/horoscope/entries", data);
  },

  async bulkCreateEntries(
    entries: CreateHoroscopeEntryData[],
  ): Promise<BulkCreateResponse> {
    return api.post<BulkCreateResponse>("/horoscope/entries/bulk", {
      entries,
    });
  },

  async updateEntry(
    id: string,
    data: UpdateHoroscopeEntryData,
  ): Promise<HoroscopeEntry> {
    return api.patch<HoroscopeEntry>(`/horoscope/entries/${id}`, data);
  },

  async deleteEntry(id: string): Promise<void> {
    return api.delete(`/horoscope/entries/${id}`);
  },

  async updateEntryStatus(
    id: string,
    data: UpdateHoroscopeEntryStatusData,
  ): Promise<HoroscopeEntry> {
    return api.patch<HoroscopeEntry>(`/horoscope/entries/${id}/status`, data);
  },

  async reviewEntry(
    id: string,
    data: ReviewHoroscopeEntryData,
  ): Promise<HoroscopeEntry> {
    return api.post<HoroscopeEntry>(`/horoscope/entries/${id}/review`, data);
  },
};
